// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — the pure game state. No DOM, no canvas, no clock, no network: a
// country name in, a new state out. This is what the tests, the sim and the solver
// all drive. All routing facts come from the border graph in data.ts.

import { bfsDist, borders, country, neighbors, type Country } from './data';
import { chainStartFor, pairFor } from './seed';
import { guessableIds, type Mode } from './modes';

export type EndReason = 'solved' | 'out' | 'stuck' | 'timeout' | 'gaveup';

/** The outcome of naming a country. */
export type PlayKind = 'hop' | 'arrive' | 'misfire' | 'repeat' | 'invalid' | 'noop';

export interface GameState {
  modeId: string;
  objective: 'route' | 'chain';
  seed: number;
  startId: string;
  /** null in Chain mode. */
  destId: string | null;
  /** BFS optimal hop count (0 in Chain). */
  par: number;
  /** The chain of country ids; chain[0] === startId. */
  chain: string[];
  lives: number;
  misfires: number;
  /** The country this round's one hint named, or null if none was taken. */
  hintId: string | null;
  solved: boolean;
  over: boolean;
  reason: EndReason | null;
}

export function createGame(mode: Mode, seed: number): GameState {
  const base = {
    modeId: mode.id,
    objective: mode.objective,
    seed: seed >>> 0,
    lives: mode.lives,
    misfires: 0,
    hintId: null as string | null,
    solved: false,
    over: false,
    reason: null as EndReason | null,
  };
  if (mode.objective === 'chain') {
    const start = chainStartFor(mode, seed);
    return { ...base, startId: start.id, destId: null, par: 0, chain: [start.id] };
  }
  const pair = pairFor(mode, seed);
  return { ...base, startId: pair.startId, destId: pair.destId, par: pair.par, chain: [pair.startId] };
}

export function tail(state: GameState): string {
  return state.chain[state.chain.length - 1];
}

export function hops(state: GameState): number {
  return state.chain.length - 1;
}

/** Over par (Trek/Odyssey): 0 = perfect route, N = N hops longer than optimal. */
export function overPar(state: GameState): number {
  return Math.max(0, hops(state) - state.par);
}

/** How close the chain ever got to the destination (min BFS distance reached). */
export function bestProgress(state: GameState): number {
  if (!state.destId) return 0;
  let best = Infinity;
  for (const id of state.chain) best = Math.min(best, bfsDist(id, state.destId));
  return best;
}

export function inChain(state: GameState, id: string): boolean {
  return state.chain.includes(id);
}

/** True in Chain mode once the tail has no un-used neighbour. */
export function isStuck(state: GameState): boolean {
  const t = tail(state);
  const used = new Set(state.chain);
  return neighbors(t).every((v) => used.has(v));
}

/** Classify what naming `id` would do — pure, exported for tests + UI hints. */
export function classify(state: GameState, id: string): PlayKind {
  if (state.over || !guessableIds().has(id)) return 'invalid';
  const t = tail(state);
  if (id === t) return 'noop';
  if (inChain(state, id)) return 'repeat';
  if (borders(t, id)) return state.objective === 'route' && id === state.destId ? 'arrive' : 'hop';
  return 'misfire';
}

/**
 * Apply a named country. Pure: returns a NEW state, or the SAME state if nothing
 * changed (over / not a real country / already the tail / a repeat). Callers
 * compare identity to know if anything happened.
 *
 * A hop onto a true neighbour extends the chain. Naming a country that does NOT
 * border the tail is a misfire: it costs a life and never touches the chain.
 */
export function play(state: GameState, id: string): GameState {
  const kind = classify(state, id);
  switch (kind) {
    case 'invalid':
    case 'noop':
    case 'repeat':
      return state;
    case 'arrive': {
      const chain = [...state.chain, id];
      return { ...state, chain, solved: true, over: true, reason: 'solved' };
    }
    case 'hop': {
      const chain = [...state.chain, id];
      const next: GameState = { ...state, chain };
      if (next.objective === 'chain' && isStuck(next)) {
        return { ...next, over: true, reason: 'stuck' };
      }
      return next;
    }
    case 'misfire': {
      const lives = state.lives - 1;
      const misfires = state.misfires + 1;
      if (lives <= 0) return { ...state, lives: 0, misfires, over: true, reason: 'out' };
      return { ...state, lives, misfires };
    }
  }
}

/**
 * Undo the last hop (route modes only). Free — the only penalty is a misfire. A
 * chain is a pure forward streak, so undo is a no-op there.
 */
export function undo(state: GameState): GameState {
  if (state.over || state.objective !== 'route') return state;
  if (state.chain.length <= 1) return state;
  return { ...state, chain: state.chain.slice(0, -1) };
}

/**
 * The one country a hint would name: a legal, not-yet-used neighbour of the tail —
 * on a route the one that is closest to the destination, on a chain the one that
 * keeps the most options open. Same rule the reference solver plays by (bot.ts),
 * but it only NAMES the hop; the player still has to make it. Null when the tail is
 * boxed in, which a hint cannot rescue (only Undo can).
 */
export function hintNext(state: GameState): string | null {
  if (state.over) return null;
  const t = tail(state);
  const used = new Set(state.chain);
  const pool = guessableIds();
  const options = neighbors(t).filter((v) => !used.has(v) && pool.has(v));
  if (!options.length) return null;
  let pick = options[0];
  if (state.objective === 'route' && state.destId) {
    let best = Infinity;
    for (const v of options) {
      const d = bfsDist(v, state.destId);
      if (d < best) {
        best = d;
        pick = v;
      }
    }
  } else {
    let best = -1;
    for (const v of options) {
      const onward = neighbors(v).filter((w) => !used.has(w) && w !== v).length;
      if (onward > best) {
        best = onward;
        pick = v;
      }
    }
  }
  return pick;
}

/**
 * Spend the round's single hint. It costs NO life and NO misfire — misfires rank
 * the race results, so a hint must never touch them. Returns the SAME state when
 * there is nothing to spend it on, matching play()/undo().
 */
export function takeHint(state: GameState): GameState {
  if (state.over || state.hintId !== null) return state;
  const next = hintNext(state);
  if (next === null) return state;
  return { ...state, hintId: next };
}

/**
 * The hint still worth showing: the country the round's one hint named, for as long
 * as it is a legal next hop. Goes null the moment it is played or the tail moves
 * away — a single hint buys ONE country, not a running commentary on every tail.
 */
export function activeHint(state: GameState): string | null {
  if (state.over || state.hintId === null) return null;
  const kind = classify(state, state.hintId);
  return kind === 'hop' || kind === 'arrive' ? state.hintId : null;
}

/** End the round because the player conceded. Never counts as solved. */
export function giveUp(state: GameState): GameState {
  if (state.over) return state;
  return { ...state, over: true, reason: 'gaveup' };
}

/** End the round because the race clock expired. */
export function markTimeout(state: GameState): GameState {
  if (state.over) return state;
  return { ...state, over: true, reason: 'timeout' };
}

export function startCountry(state: GameState): Country {
  return country(state.startId)!;
}
export function destCountry(state: GameState): Country | null {
  return state.destId ? (country(state.destId) ?? null) : null;
}
